/** Rebinding (docs/spec/07 §3): two keys per action, a key used once only. Pure. */

import type { Action, Bindings } from '../../platform/options';
import { ACTIONS } from '../../platform/options';

export const ACTION_LABELS: Readonly<Record<Action, string>> = {
  up: 'UP',
  down: 'DOWN',
  left: 'LEFT',
  right: 'RIGHT',
  fire: 'GRAB / FIRE',
  pause: 'PAUSE',
};

/** Keys the menus need for themselves: binding them would leave no way to confirm or go back. */
const RESERVED = ['Enter', 'NumpadEnter', 'Tab', 'Backspace', 'Delete', 'MetaLeft', 'MetaRight', 'ContextMenu'];

/** Whether `code` may be bound to `action` (Escape only to pause, where it lives by default). */
export function canBind(action: Action, code: string): boolean {
  if (!code || RESERVED.includes(code)) return false;
  return code !== 'Escape' || action === 'pause';
}

/** Put `code` in the action's slot, taking it off anything else that had it. */
export function bindKey(b: Bindings, action: Action, slot: 0 | 1, code: string): Bindings {
  const out = {} as Record<Action, readonly [string, string]>;
  for (const a of ACTIONS) {
    const [k0, k1] = b[a];
    out[a] = [k0 === code ? '' : k0, k1 === code ? '' : k1];
  }
  const cur = out[action];
  out[action] = slot === 0 ? [code, cur[1]] : [cur[0], code];
  return out;
}

export function clearKey(b: Bindings, action: Action, slot: 0 | 1): Bindings {
  const cur = b[action];
  return { ...b, [action]: slot === 0 ? ['', cur[1]] : [cur[0], ''] };
}

/** Actions left with no key at all (the screen warns about them). */
export function unbound(b: Bindings): Action[] {
  return ACTIONS.filter((a) => b[a][0] === '' && b[a][1] === '');
}
